import { getIsNewDb } from "../../../lib/is-new-db";

/**
 * 層別化 (subgrouping) の主体属性条件。
 *
 * legacy (is_new_db=false) は age/gender 固定 (旧 DB は主体属性が age/gender/group 前提)。
 * 新 DB (is_new_db=true) は取込時に確定した層別化属性 (querybar v3 の subject_attrs) から
 * 動的に組む。属性値は整数コード前提で「1 値 = 1 レベル (min=max)」にする。
 */

/** legacy 固定 (age/gender)。 */
export const LEGACY_SUBJECT_SUBGROUPINGS = [
  {
    label: "age",
    levels: [
      { level: 1, name: "20・30代", min: 2, max: 3 },
      { level: 2, name: "40・50代", min: 4, max: 5 },
      { level: 3, name: "60代以上", min: 6, max: 8 },
    ],
  },
  {
    label: "gender",
    levels: [
      { level: 1, name: "男性", min: 0, max: 0 },
      { level: 2, name: "女性", min: 1, max: 1 },
    ],
  },
];

/** queryContext.subject_attrs から 1 値=1 レベル (min=max) で組む (整数値前提)。 */
function fromSubjectAttrs(queryContext) {
  const attrs = queryContext?.subject_attrs ?? [];
  return attrs
    .map((item) => ({
      label: item.key,
      levels: (item.attrs ?? [])
        .map((o) => ({ value: Number(o.key), name: o.label ?? String(o.key) }))
        .filter((o) => Number.isFinite(o.value))
        .sort((a, b) => a.value - b.value)
        .map((o, i) => ({
          level: i + 1,
          name: o.name,
          min: o.value,
          max: o.value,
        })),
    }))
    .filter((c) => c.levels.length > 0);
}

/**
 * 既存の subgrouping_conditions のうち age/gender を新 DB のデータ由来条件へ差し替える。
 * 先頭に付く treatment stratificationSubgroup 等 (label が age/gender 以外) は保持する。
 * legacy のときは何もせず元の配列を返す。
 */
export function applySubgroupingConditions(conditions, queryContext) {
  if (!getIsNewDb()) return conditions;
  const kept = (conditions ?? []).filter(
    (c) => c && c.label !== "age" && c.label !== "gender"
  );
  return [...kept, ...fromSubjectAttrs(queryContext)];
}

/** 現在の主体属性ラベル一覧。legacy は age/gender 固定、新 DB は subject_attrs 由来。 */
export function getSubjectAttrLabels(queryContext) {
  if (!getIsNewDb()) return LEGACY_SUBJECT_SUBGROUPINGS.map((c) => c.label);
  return (queryContext?.subject_attrs ?? []).map((item) => item.key);
}

/**
 * subgrouping_conditions を「変数 subgrouping」と「属性 subgrouping」に分ける。
 *
 * 配列は必ず `[...変数, ...属性]` の順で組まれる (makeDataset / stratificationDo の
 * 両方とも `applySubgroupingConditions` を通す)。そこで末尾から attrLabels に
 * 一致する連続ブロックを属性とみなす。末尾が一致しない (取込属性が入れ替わった
 * 古いレポートを開いた等) ときは legacy の位置規約
 * 「3 件以上なら先頭 1 件が変数、それ以外は全部属性」へフォールバックする。
 */
export function splitSubgroupingConditions(conditions, attrLabels) {
  const list = (conditions ?? []).filter(Boolean);
  const attrSet = new Set(attrLabels ?? []);

  let head = list.length;
  while (head > 0 && attrSet.has(list[head - 1].label)) head--;
  if (head < list.length) {
    return { variables: list.slice(0, head), attributes: list.slice(head) };
  }

  return list.length > 2
    ? { variables: [list[0]], attributes: list.slice(1) }
    : { variables: [], attributes: list };
}

/**
 * 属性 subgrouping のテーブル表示順 (左の列ほど外側)。
 *
 * conditions は作成順 (legacy は `[age, gender]`、新 DB は subject_attrs 順) だが、
 * 表示は 性別 → 年代 のように逆順で、最後の属性が最外 = セル結合される左端の列に
 * なる。PDF 側の既定ラベル (性別・年代) とも一致する。
 */
export function orderAttrConditionsForDisplay(attrConditions) {
  return [...(attrConditions ?? [])].reverse();
}

/**
 * 表示順の属性レベルで行を並べ替える (legacy の 男性→女性 × 年代昇順 を再現)。
 * level_names に無い値は各属性の末尾へ送る。
 */
export function sortRowsByAttrLevels(rows, displayAttrConditions) {
  const conds = (displayAttrConditions ?? []).filter((c) => c && c.levels);
  const list = [...(rows ?? [])];
  if (conds.length === 0) return list;

  const rank = (row, cond) => {
    const i = cond.levels.findIndex(
      (lv) => lv.name === row?.level_names?.[cond.label]
    );
    return i < 0 ? cond.levels.length : i;
  };

  return list.sort((a, b) => {
    for (const cond of conds) {
      const diff = rank(a, cond) - rank(b, cond);
      if (diff !== 0) return diff;
    }
    return 0;
  });
}
